/**
 * Exchange Offer
 *  1. PDP pincode checker — validates the entered pincode against the
 *     serviceable list rendered by snippets/exchange-pincode-checker.liquid
 *     and reveals the coupon on success.
 *  2. Cart banner — copies the coupon, or pushes it into the theme's
 *     existing <form is="cart-discount"> so cart.js applies it.
 *
 * All handlers are delegated from document, because both the cart drawer
 * and the main cart are re-rendered with innerHTML (see cart.js), which
 * does not re-execute inline scripts.
 */
(function () {
  if (window.__exchangeOfferInit) return;
  window.__exchangeOfferInit = true;

  function getPincodeList(widget) {
    if (widget.__pincodes) return widget.__pincodes;

    var source = widget.querySelector('[data-exchange-pincodes]');
    var list = [];

    if (source) {
      var raw = source.textContent || '';
      // The Liquid side emits the setting through the `json` filter, so the
      // node holds a quoted JSON string.
      try {
        raw = JSON.parse(raw);
      } catch (e) {
        /* fall back to the raw text */
      }

      list = String(raw)
        .split(/[\s,;]+/)
        .map(function (pin) {
          return pin.trim();
        })
        .filter(Boolean);
    }

    widget.__pincodes = list;
    return list;
  }

  function setMessage(widget, text, state) {
    var msg = widget.querySelector('[data-exchange-msg]');
    if (!msg) return;

    msg.textContent = text;
    msg.classList.remove('exchange-pincode__msg--ok', 'exchange-pincode__msg--err');
    if (state) msg.classList.add('exchange-pincode__msg--' + state);
    msg.hidden = !text;
  }

  function toggleCoupon(widget, show) {
    var coupon = widget.querySelector('[data-exchange-coupon]');
    if (coupon) coupon.hidden = !show;
  }

  function checkPincode(widget) {
    var input = widget.querySelector('[data-exchange-input]');
    if (!input) return;

    var pincode = (input.value || '').trim();

    if (!/^\d{6}$/.test(pincode)) {
      toggleCoupon(widget, false);
      setMessage(widget, widget.dataset.invalidText || 'Please enter a valid 6-digit pincode', 'err');
      return;
    }

    var serviceable = getPincodeList(widget).indexOf(pincode) !== -1;

    toggleCoupon(widget, serviceable);
    setMessage(
      widget,
      serviceable ? widget.dataset.successText : widget.dataset.failureText,
      serviceable ? 'ok' : 'err'
    );
  }

  /**
   * Hands the code to the theme's cart discount form so cart.js owns the
   * request, the duplicate check and the "not applicable" error. Returns
   * false when no form is on the page, so the caller can fall back to copy.
   */
  function applyDiscount(root, code) {
    var scope = root.closest('cart-drawer, .cart-drawer, .drawer') || document;
    var form = scope.querySelector('form[is="cart-discount"]') || document.querySelector('form[is="cart-discount"]');
    if (!form) return false;

    var input = form.querySelector('input[name="discount"]');
    if (!input) return false;

    input.value = code;
    // Not requestSubmit(): the drawer's form lives inside a hidden modal, so
    // native validation would fail on a non-focusable required field.
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    return true;
  }

  function copyCode(button, code) {
    var original = button.dataset.originalLabel || button.textContent;
    button.dataset.originalLabel = original;

    var done = function () {
      button.textContent = button.dataset.copiedLabel || 'Copied';
      setTimeout(function () {
        button.textContent = original;
      }, 2000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, function () {});
      return;
    }

    var helper = document.createElement('textarea');
    helper.value = code;
    helper.setAttribute('readonly', '');
    helper.style.position = 'absolute';
    helper.style.left = '-9999px';
    document.body.appendChild(helper);
    helper.select();
    try {
      document.execCommand('copy');
      done();
    } catch (e) {
      /* nothing else to try */
    }
    document.body.removeChild(helper);
  }

  document.addEventListener('click', function (event) {
    var checkBtn = event.target.closest('[data-exchange-check]');
    if (checkBtn) {
      var widget = checkBtn.closest('[data-exchange-widget]');
      if (widget) checkPincode(widget);
      return;
    }

    var copyBtn = event.target.closest('[data-exchange-copy]');
    if (copyBtn) {
      copyCode(copyBtn, copyBtn.dataset.exchangeCopy);
      return;
    }

    var applyBtn = event.target.closest('[data-exchange-apply]');
    if (applyBtn) {
      if (!applyDiscount(applyBtn, applyBtn.dataset.exchangeApply)) {
        copyCode(applyBtn, applyBtn.dataset.exchangeApply);
      }
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;

    var input = event.target.closest('[data-exchange-input]');
    if (!input) return;

    event.preventDefault();
    var widget = input.closest('[data-exchange-widget]');
    if (widget) checkPincode(widget);
  });

  document.addEventListener('input', function (event) {
    var input = event.target.closest('[data-exchange-input]');
    if (!input) return;

    var cleaned = input.value.replace(/\D/g, '').slice(0, 6);
    if (cleaned !== input.value) input.value = cleaned;

    var widget = input.closest('[data-exchange-widget]');
    if (widget) {
      setMessage(widget, '');
      toggleCoupon(widget, false);
    }
  });

  /**
   * The check is per-visit: every load starts from a blank field with no
   * result. Browsers restore input values on reload and on bfcache restore,
   * which would otherwise leave a stale pincode sitting above a stale
   * verdict, so both are cleared explicitly.
   */
  function reset() {
    document.querySelectorAll('[data-exchange-widget]').forEach(function (widget) {
      var input = widget.querySelector('[data-exchange-input]');
      if (input) input.value = '';
      setMessage(widget, '');
      toggleCoupon(widget, false);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reset);
  } else {
    reset();
  }

  window.addEventListener('pageshow', reset);
})();